/**
 * CriticalBanner component.
 * Pinned alert bar shown when the user has >=1 unread CRITICAL-category
 * notification (EMERGENCY_HOLIDAY, FEE_DUE). Session-dismissible only —
 * reappears on next login while the item(s) remain unread (AuthContext
 * clears the dismiss flag on both login and logout). Viewing or dismissing
 * this banner NEVER marks anything read; only the bell panel or the full
 * notifications page do that.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import api from '../../services/api';
import { CRITICAL_TYPES } from '../../utils/notification-category';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export const CRITICAL_BANNER_DISMISS_KEY = 'critical-banner-dismissed';

interface CriticalBannerProps {
  /** Portal-specific path to the full notifications list page. */
  notificationsPath: string;
}

/**
 * CriticalBanner — pinned, session-dismissible alert for unread CRITICAL notifications.
 * @param props - notificationsPath for the "View" deep-link
 * @returns JSX element, or null when there's nothing to show
 */
export default function CriticalBanner({ notificationsPath }: CriticalBannerProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(
    () => sessionStorage.getItem(CRITICAL_BANNER_DISMISS_KEY) === '1'
  );

  const { data: criticalItems = [] } = useQuery({
    queryKey: ['notifications', 'critical-unread'],
    queryFn: async () => {
      const response = await api.get('/notifications', { params: { isRead: 'false', limit: 50 } });
      const notifications: NotificationItem[] = response.data?.data?.notifications ?? [];
      return notifications.filter((n) => CRITICAL_TYPES.includes(n.type));
    },
    refetchInterval: 30_000,
  });

  if (dismissed || criticalItems.length === 0) {
    return null;
  }

  const [latest, ...rest] = criticalItems;

  const handleDismiss = (): void => {
    sessionStorage.setItem(CRITICAL_BANNER_DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div style={{ padding: '12px 16px 0' }}>
      <div
        data-testid="critical-banner"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '14px 16px',
          background: 'var(--red-lt)',
          border: '1.5px solid var(--red)',
          borderRadius: 14,
          boxShadow: '0 2px 10px rgba(220,38,38,0.12)',
          fontFamily: 'var(--font-body)',
          // Own stacking context so the View/dismiss controls at the banner's
          // right edge stay clickable above any sibling page content.
          position: 'relative',
          zIndex: 41,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: 'var(--red)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={20} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--red)', letterSpacing: '-0.01em' }}>
            {latest.title}
            {rest.length > 0 && (
              <span style={{ fontWeight: 700, opacity: 0.85 }}> (and {rest.length} more)</span>
            )}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--t2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {latest.message}
          </div>
        </div>
        <button
          onClick={() => navigate(notificationsPath)}
          style={{
            flexShrink: 0,
            height: 34,
            padding: '0 16px',
            borderRadius: 9,
            border: 'none',
            background: 'var(--red)',
            color: '#fff',
            fontSize: 12.5,
            fontWeight: 800,
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap',
          }}
        >
          View
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          style={{
            flexShrink: 0,
            width: 32,
            height: 32,
            borderRadius: 9,
            border: 'none',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--red)',
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
